import CustomerListRowItem from "@/components/customers/list/CustomerListRowItem";
import { SortableHeader } from "@/components/shared/SortableHeader";
import {
  DEFAULT_SORT_DIR,
  groupCustomerRows,
  URGENCY_LABELS,
  type CustomerListRow,
  type CustomerListSort,
  type CustomerListSortKey,
} from "@/lib/customer-list-calc";

interface CustomerListV2TableProps {
  rows: CustomerListRow[];
  /** null = «Haster mest»: gruppert etter hast. Ellers flat sortering på kolonnen. */
  sort: CustomerListSort | null;
  onSort: (sort: CustomerListSort) => void;
  emptyMessage: string;
  onClaimOwnership: (customerId: string) => void;
}

const HEADERS: Array<{ label: string; key?: CustomerListSortKey; align?: "right" }> = [
  { label: "Kunde", key: "name" },
  { label: "Status", key: "status" },
  { label: "Eier" },
  { label: "Neste steg", key: "next_step" },
  { label: "GEEK i pipe", key: "pipe", align: "right" },
  { label: "GEEK levert siste 12 mnd", key: "delivered", align: "right" },
  { label: "Siste kontakt", key: "last_contact" },
];

/** Tabellen i kundeliste v2. Org.nr og e-post er bare i søket, ikke kolonner. */
export default function CustomerListV2Table({ rows, sort, onSort, emptyMessage, onClaimOwnership }: CustomerListV2TableProps) {
  const groups = sort ? [{ group: null, rows }] : groupCustomerRows(rows);
  const toggle = (key: CustomerListSortKey) => {
    if (sort?.key === key) onSort({ key, dir: sort.dir === "asc" ? "desc" : "asc" });
    else onSort({ key, dir: DEFAULT_SORT_DIR[key] });
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs sm:text-sm">
        <thead>
          <tr className="border-b">
            {HEADERS.map((h) =>
              h.key ? (
                <SortableHeader
                  key={h.label}
                  label={h.label}
                  active={sort?.key === h.key}
                  dir={sort?.key === h.key ? sort.dir : DEFAULT_SORT_DIR[h.key]}
                  onToggle={() => toggle(h.key!)}
                  align={h.align ?? "left"}
                  className="py-2 px-3 text-xs uppercase tracking-wider whitespace-nowrap"
                />
              ) : (
                <th key={h.label} className="py-2 px-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground whitespace-nowrap">
                  {h.label}
                </th>
              ),
            )}
          </tr>
        </thead>
        {rows.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={HEADERS.length} className="p-4 text-sm text-muted-foreground">{emptyMessage}</td>
            </tr>
          </tbody>
        ) : (
          groups.map((g) => (
            <tbody key={g.group ?? "flat"}>
              {g.group && (
                <tr>
                  <th
                    scope="rowgroup"
                    colSpan={HEADERS.length}
                    className="bg-muted/50 px-3 py-1.5 text-left text-[11px] font-medium uppercase tracking-wider text-foreground"
                  >
                    {URGENCY_LABELS[g.group]} · {g.rows.length}
                  </th>
                </tr>
              )}
              {g.rows.map((row) => (
                <CustomerListRowItem key={row.id} row={row} onClaimOwnership={onClaimOwnership} />
              ))}
            </tbody>
          ))
        )}
      </table>
    </div>
  );
}
